// The legal texts of the website footer ("Richtlinienlinks") can be shown as
// pages of their own instead of a pop-up (Asana 1212190375734539). The
// website renderer serves them at these paths, in the order of
// footer.footer_links.pp_links.links (Datenschutz, Impressum, AGB).
// Keep in sync with funnelhub-backend common/enum.js (RESERVED_WEBSITE_TAB_ROUTES)
// and funnel-customer-site src/lib/legal-pages.js: the three repos share no
// package, the paths are a fixed contract (same as Website v2).
const LEGAL_PAGE_SLUGS = ["datenschutz", "impressum", "agb"];

// No website tab may use a legal page path, the page would hide the tab
const RESERVED_WEBSITE_TAB_ROUTES = LEGAL_PAGE_SLUGS.map((slug) => `/${slug}`);

// The one form of a tab route: lowercase, trimmed, no trailing slash. Same
// name and rule as toStoredTabRoute in funnelhub-backend
// helper/website-tab-route.helper.js, which stores routes this way.
const toStoredTabRoute = (tabRoute) =>
  tabRoute
    .trim()
    .toLowerCase()
    .replace(/(.)\/+$/, "$1") || "/";

export const isReservedWebsiteTabRoute = (tabRoute) =>
  typeof tabRoute === "string" &&
  RESERVED_WEBSITE_TAB_ROUTES.includes(toStoredTabRoute(tabRoute));

// A legal page path is refused only for a new tab: duplicating onto an active
// tab the target customer already has stays allowed, as in the backend
// (review rounds 3 to 5). existingTabRoutes: the target customer's active tab
// routes; without such a list (not loaded, failed) the backend decides.
export const isRefusedReservedTabRoute = (tabRoute, existingTabRoutes) => {
  if (!isReservedWebsiteTabRoute(tabRoute)) return false;
  if (!Array.isArray(existingTabRoutes)) return false;
  return !existingTabRoutes.some(
    (existing) =>
      typeof existing === "string" &&
      toStoredTabRoute(existing) === toStoredTabRoute(tabRoute)
  );
};

// Tab routes of a customer (duplicate dialog: active ones; customer view: all).
// Only the answer of the latest call counts (requestRef); a failed answer or
// one that is no list goes to onFailed, an outdated one is ignored (review
// rounds 4, 5 and 7).
export const loadTargetTabRoutes = async ({
  customerId,
  requestRef,
  fetchTabRoutes,
  onLoaded,
  onFailed
}) => {
  const request = ++requestRef.current;
  let response;
  try {
    response = await fetchTabRoutes(customerId);
  } catch (error) {
    if (request === requestRef.current) onFailed(error);
    return;
  }
  if (request !== requestRef.current) return;
  const tabRoutes = response?.data?.data;
  if (!Array.isArray(tabRoutes)) {
    onFailed(new Error("No tab list"));
    return;
  }
  onLoaded(tabRoutes);
};

// Text, or an image / embed without text (e.g. a scanned Impressum). The one
// copy in the admin UI (publish rule and help text); same rule as
// hasVisibleText in funnel-customer-site src/lib/legal-pages.js, which decides
// whether the page exists (the repos share no package).
export const hasVisibleText = (html) =>
  typeof html === "string" &&
  (/<(img|iframe|video)\b/i.test(html) ||
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim() !== "");

// Index of the first legal text in the footer that would become an empty page
// (shown, no "Bereich" link, no text), or -1.
export const findLegalPageWithoutContent = (ppLinks) => {
  if (!ppLinks?.showAsPages) return -1;
  const shownLinks = ppLinks.agbLinkEnabled ? 3 : 2;
  for (let index = 0; index < shownLinks; index++) {
    const link = ppLinks.links?.[index];
    if (!link?.link && !hasVisibleText(link?.content)) return index;
  }
  return -1;
};

// URLs the renderer serves as pages, for the help text, e.g.
// kunde.de/wolf-metall/impressum: shown texts without "Bereich" link and with
// content (same rule as getLegalPage in the renderer).
export const legalPageUrls = ({ domain, tabRoute, ppLinks }) => {
  if (!ppLinks?.showAsPages) return [];
  const nestedWebsiteRoute = tabRoute && tabRoute !== "/" ? tabRoute : "";
  const shownLinks = ppLinks.agbLinkEnabled ? 3 : 2;
  return LEGAL_PAGE_SLUGS.filter((slug, index) => {
    const link = ppLinks.links?.[index];
    return index < shownLinks && !link?.link && hasVisibleText(link?.content);
  }).map((slug) => `${domain}${nestedWebsiteRoute}/${slug}`);
};
